import type { FC } from 'react';
import { documentTypeLabels } from 'src/apis/document-exchange/mappings';
import type {
  OperatorDocument,
  OperatorDocumentTableList,
} from 'src/apis/document-exchange/types';
import { Button } from 'src/components/buttons-and-actions/button';
import { FormatDate } from 'src/components/data-display/format-date';
import { Table } from 'src/components/data-display/smart-table';
import { StatusDisplay } from 'src/components/data-display/status-display';
import { SpecificSortHeader, Td, Th } from 'src/components/data-display/table';
import { DownloadDocumentLink } from 'src/pages/shippers/documents/components/download-document-link';
import { DocumentActions } from 'src/pages/shippers/documents/components/table-actions';
import type { PageParams } from 'src/pages/shippers/documents/use-page-params';
import {
  documentStatusLabels,
  variantMap,
} from 'src/pages/shippers/documents/utils';

type Props = {
  type: 'INBOX' | 'OUTBOX';
  pageParams: PageParams;
  documents: OperatorDocumentTableList;
  refreshDocuments?: () => void;
  pending: boolean;
  showTableActions?: boolean;
};

export const DocumentsTable: FC<Props> = ({
  type,
  pageParams,
  documents,
  refreshDocuments,
  pending,
  showTableActions = true,
}) => {
  return (
    <Table
      data-testid="documents-table"
      pending={pending}
      paginated={documents}
      data={documents._embedded.items}
      setId={(data) => data.operatorDocumentId}
      cols={[
        {
          key: 'changedAt',
          head: (
            <SpecificSortHeader
              label="Date"
              column="CHANGED_AT"
              params={pageParams}
              sortParam="sortBy"
              directionParam="sortDirection"
              data-testid="date-header-sort"
            />
          ),
          body: (data, index) => (
            <Td data-testid={`changedAt-${index}`}>
              <FormatDate value={data.changedAt} type="date-time" />
            </Td>
          ),
        },
        {
          key: 'name',
          head: <Th>{pageParams.filter.documentName.label}</Th>,
          body: (data, index) => (
            <Td data-testid={`name-${index}`}>
              {data._links.getCurrentOperatorDocumentFile ? (
                <DownloadDocumentLink
                  getCurrentOperatorDocumentFile={
                    data._links.getCurrentOperatorDocumentFile.href
                  }
                >
                  {data.documentName}
                </DownloadDocumentLink>
              ) : (
                <>{data.documentName}</>
              )}
            </Td>
          ),
        },
        {
          key: 'description',
          head: <Th>{pageParams.filter.documentDescription.label}</Th>,
          body: (data, index) => (
            <Td data-testid={`description-${index}`}>
              {data.documentDescription}
            </Td>
          ),
        },
        {
          key: 'type',
          head: <Th>{pageParams.filter.documentType.label}</Th>,
          body: (data, index) => (
            <Td data-testid={`type-${index}`}>
              {documentTypeLabels[data.documentType]}
            </Td>
          ),
        },
        ...(type === 'OUTBOX'
          ? [
              {
                key: 'status',
                head: <Th>{pageParams.filter.documentStatus.label}</Th>,
                body: (data: OperatorDocument, index: number) => (
                  <Td>
                    <StatusDisplay
                      status={documentStatusLabels[data.documentStatus]}
                      variant={variantMap[data.documentStatus]}
                      data-testid={`status-${index}`}
                    />
                  </Td>
                ),
              },
              ...(showTableActions
                ? [
                    {
                      key: 'actions',
                      width: '8rem',
                      head: <Th align="center">Actions</Th>,
                      body: (data: OperatorDocument, index: number) => (
                        <Td data-testid={`actions-${index}`} align="center">
                          <DocumentActions
                            data={data}
                            refreshDocuments={refreshDocuments}
                            index={index}
                          />
                        </Td>
                      ),
                    },
                  ]
                : []),
            ]
          : []),
        ...(type === 'INBOX'
          ? [
              {
                key: 'operatorName',
                head: <Th>Operator</Th>,
                body: (data: OperatorDocument, index: number) => (
                  <Td data-testid={`operator-name-${index}`}>
                    {data.operatorName}
                  </Td>
                ),
              },
            ]
          : []),
      ]}
      empty={
        pageParams.hasChangedFilters
          ? {
              title: 'No Matching Results',
              description:
                'No results match your search criteria. Try changing or resetting the filters.',
              actions: (
                <Button
                  data-testid="empty-action-button"
                  onClick={pageParams.resetFilters}
                >
                  Reset Filters
                </Button>
              ),
            }
          : {
              title: 'No Documents',
              description:
                type === 'INBOX'
                  ? 'No documents have been sent by operators in this period.'
                  : 'No documents sent to shippers in this period.',
            }
      }
    />
  );
};
